import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, Home } from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error("Failed to check status");
      }

      const data = await response.json();

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.payment_status === "expired" || data.payment_status === "failed") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 mx-auto" />
        </div>

        <div className="card-dark p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-[#00E073] mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-semibold text-white mb-2">Vérification du paiement...</h1>
              <p className="text-[#94A3B8]">Veuillez patienter quelques instants</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-[#00E073]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-[#00E073]" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Paiement confirmé !</h1>
              <p className="text-[#94A3B8] mb-6">
                Votre intervention est maintenant confirmée. L'infirmier a été notifié et interviendra à la date et l'heure prévues.
              </p>
              <Link to="/dashboard">
                <Button className="btn-primary">
                  <Home className="w-4 h-4 mr-2" />
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Vérification en cours</h1>
              <p className="text-[#94A3B8] mb-6">
                Le paiement est en cours de traitement. Vous recevrez une confirmation par email.
              </p>
              <Link to="/dashboard">
                <Button className="btn-secondary">
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Paiement non abouti</h1>
              <p className="text-[#94A3B8] mb-6">
                Le paiement n'a pas pu être complété. Veuillez réessayer.
              </p>
              <Link to="/dashboard">
                <Button className="btn-primary">
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}
        </div>

        <div className="legal-mention mt-6 text-center">
          <p className="text-xs">
            Le paiement des soins est effectué directement à l'infirmier. 
            Infirmiers Privés perçoit uniquement des frais de service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
